import { useEffect, useRef, useCallback, useState } from 'react'
import { Loader } from '@googlemaps/js-api-loader'

const API_KEY = 'AIzaSyCz4oJ8UcC7BwnuaYgk0wBIPCQvvGpbrHI'
const DEFAULT_LAT = 35.6895014
const DEFAULT_LNG = 139.6917337
const ANAMNE_PIN_URL = '/images/google_map/anamne.svg'
const OTHER_PIN_URL = '/images/google_map/other.svg'
const PIN_SIZE = 24
const SELECTED_PIN_SIZE = 30

type Pharmacy = {
  id: number
  name: string
  address: string
  lat: number | null
  lng: number | null
  is_alliance: boolean
}

type Props = {
  pharmacies: Pharmacy[]
  centerLat?: number
  centerLng?: number
  selectedPharmacyId?: number | null
  onSelectPharmacy?: (pharmacyId: number) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MapInstance = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MarkerInstance = any

export default function PharmacyMap({
  pharmacies,
  centerLat,
  centerLng,
  selectedPharmacyId,
  onSelectPharmacy,
}: Props) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<MapInstance>(null)
  const markersRef = useRef<{ marker: MarkerInstance; content: HTMLElement; handler: () => void }[]>([])
  const infoWindowRef = useRef<MapInstance>(null)
  const loaderRef = useRef<Loader | null>(null)
  const [mapReady, setMapReady] = useState(false)

  const getLoader = useCallback(() => {
    if (!loaderRef.current) {
      loaderRef.current = new Loader({
        apiKey: API_KEY,
        version: 'beta',
        libraries: ['marker'],
      })
    }
    return loaderRef.current
  }, [])

  // マップ初期化（1回のみ）
  useEffect(() => {
    if (!mapRef.current) return

    const loader = getLoader()

    loader
      .importLibrary('maps')
      .then((mapsLib: any) => {
        const map = new mapsLib.Map(mapRef.current!, {
          center: {
            lat: centerLat ?? DEFAULT_LAT,
            lng: centerLng ?? DEFAULT_LNG,
          },
          zoom: 14,
          mapId: 'ANAMNE_MAP_ID',
          styles: [
            {
              featureType: 'poi',
              stylers: [{ visibility: 'off' }],
            },
          ],
        })
        mapInstanceRef.current = map
        infoWindowRef.current = new mapsLib.InfoWindow()
        setMapReady(true)
      })
      .catch((e: unknown) => console.error('Google Maps の読み込みに失敗しました:', e))
    // 初期化は1回のみ（centerLat/centerLngの変更はマーカー更新時に対応）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLoader])

  // 中心座標の更新 + マーカー更新（マップ準備完了後に実行）
  useEffect(() => {
    if (!mapReady) return

    const map = mapInstanceRef.current
    if (!map) return

    // 既存マーカーを削除（イベントリスナーもクリーンアップ）
    markersRef.current.forEach(({ marker, content, handler }: { marker: MarkerInstance; content: HTMLElement; handler: () => void }) => {
      marker.removeEventListener('gmp-click', handler)
      content.removeEventListener('click', handler)
      marker.map = null
    })
    markersRef.current = []

    const loader = getLoader()

    loader
      .importLibrary('marker')
      .then(({ AdvancedMarkerElement }: any) => {
        const validPharmacies = pharmacies.filter((p) => p.lat != null && p.lng != null)

        validPharmacies.forEach((pharmacy) => {
          const isSelected = pharmacy.id === selectedPharmacyId
          const img = document.createElement('img')
          img.src = pharmacy.is_alliance ? ANAMNE_PIN_URL : OTHER_PIN_URL
          img.width = isSelected ? SELECTED_PIN_SIZE : PIN_SIZE
          img.style.cursor = 'pointer'
          if (isSelected) {
            img.style.filter =
              'drop-shadow(1px 0 0 #fff) drop-shadow(-1px 0 0 #fff) drop-shadow(0 1px 0 #fff) drop-shadow(0 -1px 0 #fff) drop-shadow(0 2px 3px rgba(0,0,0,0.25))'
          }

          let content: HTMLElement = img
          if (isSelected) {
            const wrapper = document.createElement('div')
            wrapper.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;'
            const label = document.createElement('span')
            label.textContent = '選択中'
            label.style.cssText = 'background:#DC2626;color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:9999px;margin-bottom:1px;white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,0.25);line-height:1.2;'
            wrapper.append(label, img)
            content = wrapper
          }

          const marker = new AdvancedMarkerElement({
            map,
            position: { lat: pharmacy.lat!, lng: pharmacy.lng! },
            content,
            title: pharmacy.name,
            zIndex: isSelected ? 999 : undefined,
          })

          const handleMarkerClick = () => {
            const infoWindow = infoWindowRef.current
            if (infoWindow) {
              const wrapper = document.createElement('div')
              wrapper.style.padding = '4px'
              const nameEl = document.createElement('p')
              nameEl.style.fontWeight = '600'
              nameEl.style.marginBottom = '4px'
              nameEl.textContent = pharmacy.name
              const addressEl = document.createElement('p')
              addressEl.style.fontSize = '13px'
              addressEl.style.color = '#666'
              addressEl.textContent = pharmacy.address
              wrapper.append(nameEl, addressEl)
              infoWindow.setContent(wrapper)
              infoWindow.open({ anchor: marker, map })
            }
            onSelectPharmacy?.(pharmacy.id)
          }

          marker.addEventListener('gmp-click', handleMarkerClick)
          content.addEventListener('click', handleMarkerClick)

          markersRef.current.push({ marker, content, handler: handleMarkerClick })
        })

        // 検索中心座標とマーカーを含むboundsでビューポートを調整
        const LatLngBounds = (window as any).google?.maps?.LatLngBounds
        if (LatLngBounds) {
          const bounds = new LatLngBounds()
          if (centerLat != null && centerLng != null) {
            bounds.extend({ lat: centerLat, lng: centerLng })
          }
          validPharmacies.forEach((p) => {
            bounds.extend({ lat: p.lat!, lng: p.lng! })
          })
          if (!bounds.isEmpty()) {
            if (validPharmacies.length <= 1 && centerLat != null && centerLng != null && validPharmacies.length === 0) {
              // 薬局0件でも検索座標があればそこを中心に表示
              map.setCenter({ lat: centerLat, lng: centerLng })
              map.setZoom(14)
            } else if (validPharmacies.length === 1 && centerLat == null) {
              // 初期表示で1件のみ
              map.setCenter({ lat: validPharmacies[0].lat!, lng: validPharmacies[0].lng! })
              map.setZoom(14)
            } else {
              map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 })
            }
          }
        }
      })
      .catch((e: unknown) => console.error('マーカーの表示に失敗しました:', e))
  }, [pharmacies, centerLat, centerLng, selectedPharmacyId, mapReady, getLoader, onSelectPharmacy])

  // 選択が変わったら選択ピンに地図をパン（ズームは維持）
  useEffect(() => {
    if (!mapReady || selectedPharmacyId == null) return
    const map = mapInstanceRef.current
    if (!map) return
    const selected = pharmacies.find((p) => p.id === selectedPharmacyId)
    if (selected?.lat != null && selected?.lng != null) {
      map.panTo({ lat: selected.lat, lng: selected.lng })
    }
  }, [selectedPharmacyId, pharmacies, mapReady])

  return (
    <div
      ref={mapRef}
      className="w-full h-64 rounded-lg border border-gray-200"
    />
  )
}
